import { useState, useMemo } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  PackageIcon, UsersIcon, ReceiptIcon, BankIcon, BookOpenIcon,
  ShoppingCartIcon, CoinsIcon, MoneyIcon, ChartLineUpIcon,
  CalendarIcon, GaugeIcon, LockKeyIcon, UserCircleIcon,
  FileTextIcon, ClipboardTextIcon, BuildingsIcon, SignOutIcon, XIcon,
  CaretRightIcon, IdentificationCardIcon, FileXlsIcon,
  WarehouseIcon, ArrowsLeftRightIcon, ShieldWarningIcon,
  CurrencyDollarIcon, WrenchIcon, ChartLineIcon, AirplaneTiltIcon,
  HandCoinsIcon, WalletIcon, ReceiptXIcon, MagnifyingGlassIcon, TargetIcon, ShieldCheckIcon, FileIcon,
  TruckIcon, FlaskIcon, MapTrifoldIcon,
} from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import { useSidebar } from "@/contexts/SidebarContext";

// ============================================================
// LISTADO OFICIAL DE 14 MÓDULOS — PRD AGROPORT (definitivo)
// ============================================================
//  1. Productos e Inventario      2. Clientes
//  3. Compras                     4. Contabilidad
//  5. Ventas y Facturación        6. Pagos y Cobranza
//  7. GRATIVA RTC                8. Calendario y Alertas
//  9. Reportería                 10. Panel del Admin General
// 11. Permisos                   12. Viáticos y Gastos
// 13. Recursos Humanos           14. Formularios
// ============================================================
// REGLA: solo estos 14 son módulos. Toda sub-función (proformas,
// cobros RTC, bodegas, RTC, checklists, QuickBooks, etc.) vive
// DENTRO de su módulo como sub-vista, NUNCA como entrada de menú.
// ============================================================
// Las SECCIONES de abajo son meros agrupadores visuales para el
// sidebar (DASHBOARD / OPERACIONES / FINANZAS / ADMINISTRACIÓN).
// NO son módulos del PRD.
// ============================================================
const DASHBOARD_ITEM = { to: "/dashboard", label: "Dashboard", icon: GaugeIcon, testid: "nav-dashboard" };

const OPERACIONES = [
  {
    key: "inventario", label: "Productos e Inventario", icon: PackageIcon, testid: "nav-inventory-group",
    matchPaths: ["/productos", "/inventario"],
    children: [
      { to: "/productos", label: "Catálogo de productos", icon: PackageIcon, testid: "nav-products" },
      { to: "/productos/lista-precios", label: "Lista de precios", icon: CurrencyDollarIcon, testid: "nav-price-list" },
      { to: "/inventario/ingresos", label: "Ingresos (recepción)", icon: TruckIcon, testid: "nav-inv-receive" },
      { to: "/inventario/transferencias", label: "Transferencias", icon: ArrowsLeftRightIcon, testid: "nav-transfers" },
      { to: "/inventario/solicitudes-traslado", label: "Solicitudes de traslado", icon: ArrowsLeftRightIcon, testid: "nav-transfer-requests" },
      { to: "/inventario/alertas", label: "Alertas de vencimiento", icon: ShieldWarningIcon, testid: "nav-expiry-alerts" },
      { to: "/inventario/ajustes", label: "Ajustes de inventario", icon: WrenchIcon, testid: "nav-inv-adjustments" },
      { to: "/inventario/conteo-fisico", label: "Conteo físico", icon: ClipboardTextIcon, testid: "nav-inv-counts" },
      { to: "/inventario/doble-lectura", label: "Doble lectura del costo", icon: ChartLineIcon, testid: "nav-double-cost", adminOnly: true },
    ],
  },
  {
    key: "clientes", label: "Clientes", icon: UsersIcon, testid: "nav-clients-group",
    matchPaths: ["/clientes", "/rtc"],
    children: [
      { to: "/clientes", label: "Catálogo de clientes", icon: IdentificationCardIcon, testid: "nav-clients" },
      { to: "/clientes/dormidos", label: "Clientes dormidos", icon: ShieldWarningIcon, testid: "nav-dormant-clients" },
      { to: "/clientes/bloqueos", label: "Bloqueos de crédito", icon: LockKeyIcon, testid: "nav-credit-blocks" },
      { to: "/clientes/importar", label: "Importar desde Excel", icon: FileXlsIcon, testid: "nav-import-clients", adminOnly: true },
      { to: "/rtc", label: "Representantes (RTC)", icon: UserCircleIcon, testid: "nav-representatives" },
    ],
  },
  {
    key: "compras", label: "Compras", icon: ShoppingCartIcon, testid: "nav-purchases-group",
    matchPaths: ["/compras", "/importaciones", "/auto-oc", "/proveedores", "/cxp"],
    children: [
      { to: "/compras", label: "Órdenes y facturas", icon: ShoppingCartIcon, testid: "nav-purchases" },
      { to: "/proveedores", label: "Proveedores", icon: BuildingsIcon, testid: "nav-suppliers" },
      { to: "/cxp", label: "Cuentas por Pagar (CxP)", icon: HandCoinsIcon, testid: "nav-payables" },
      { to: "/auto-oc", label: "Auto-OC (reorden)", icon: ChartLineUpIcon, testid: "nav-auto-oc" },
      { to: "/importaciones", label: "Importaciones", icon: AirplaneTiltIcon, testid: "nav-imports" },
    ],
  },
  { to: "/ventas", label: "Ventas y Facturación", icon: ReceiptIcon, testid: "nav-sales" },
  { to: "/cxc", label: "Pagos y Cobranza", icon: BankIcon, testid: "nav-receivables" },
  { to: "/calendario", label: "Calendario y Alertas", icon: CalendarIcon, testid: "nav-calendar" },
  { to: "/reportes", label: "Reportes", icon: ChartLineUpIcon, testid: "nav-reports", matchPaths: ["/reportes", "/centro-reportes"] },
  { to: "/solicitudes-compra", label: "Solicitudes de compra", icon: ShoppingCartIcon, testid: "nav-purchase-requests" },
];

const FINANZAS = [
  {
    key: "contabilidad", label: "Contabilidad", icon: BookOpenIcon, testid: "nav-accounting-group",
    matchPaths: ["/contabilidad", "/partidas-gasto", "/prestamos", "/gasto-distribuido", "/depositos", "/notas-credito", "/sar", "/tesoreria"],
    children: [
      { to: "/contabilidad", label: "Catálogo y partidas", icon: BookOpenIcon, testid: "nav-accounting" },
      { to: "/tesoreria", label: "Tesorería", icon: WalletIcon, testid: "nav-treasury" },
      { to: "/gasto-distribuido", label: "Gasto distribuido", icon: ChartLineIcon, testid: "nav-expense-dist" },
      { to: "/depositos", label: "Depósitos / Conciliación", icon: WalletIcon, testid: "nav-deposits" },
      { to: "/notas-credito", label: "Notas de crédito de compra", icon: ReceiptXIcon, testid: "nav-credit-notes" },
      { to: "/sar", label: "Cumplimiento fiscal SAR", icon: ShieldWarningIcon, testid: "nav-sar" },
      { to: "/partidas-gasto", label: "Cuentas de gasto", icon: ClipboardTextIcon, testid: "nav-expense-accounts" },
      { to: "/prestamos", label: "Préstamos y obligaciones", icon: HandCoinsIcon, testid: "nav-loans" },
    ],
  },
  { to: "/comisiones", label: "GRATIVA RTC", icon: CoinsIcon, testid: "nav-commissions" },
  { to: "/viaticos", label: "Viáticos y Gastos", icon: MoneyIcon, testid: "nav-expenses" },
  { to: "/metas", label: "Metas de Venta", icon: TargetIcon, testid: "nav-sales-targets" },
];

const ADMINISTRACION = [
  {
    key: "admin", label: "Panel del Admin General", icon: GaugeIcon, testid: "nav-admin-group",
    matchPaths: ["/admin", "/empresas", "/auditoria", "/configuracion"],
    children: [
      { to: "/admin", label: "Tablero ejecutivo", icon: GaugeIcon, testid: "nav-admin-dashboard" },
      { to: "/admin/landing-editor", label: "Editor de Landing", icon: FileTextIcon, testid: "nav-landing-editor" },
      { to: "/admin/backups", label: "Copias de seguridad", icon: ShieldCheckIcon, testid: "nav-backups" },
      { to: "/admin/plantillas", label: "Gestión de Plantillas", icon: FileIcon, testid: "nav-templates" },
      { to: "/admin/configuracion-documentos", label: "Configuración de documentos y datos fiscales", icon: ReceiptIcon, testid: "nav-invoice-settings" },
      { to: "/empresas", label: "Empresas y sucursales", icon: BuildingsIcon, testid: "nav-companies" },
      { to: "/inventario/bodegas", label: "Bodegas por sucursal", icon: WarehouseIcon, testid: "nav-warehouses", adminOnly: true },
      { to: "/regiones", label: "Regiones de trabajo", icon: MapTrifoldIcon, testid: "nav-regions", adminOnly: true },
      { to: "/auditoria", label: "Auditoría", icon: ClipboardTextIcon, testid: "nav-audit" },
      { to: "/configuracion", label: "Configuración general", icon: WrenchIcon, testid: "nav-settings" },
    ],
  },
  { to: "/permisos", label: "Permisos", icon: LockKeyIcon, testid: "nav-permissions" },
  { to: "/rrhh", label: "Recursos Humanos", icon: UserCircleIcon, testid: "nav-hr" },
  { to: "/formularios", label: "Formularios", icon: FileTextIcon, testid: "nav-forms" },
  { to: "/prueba", label: "Prueba", icon: FlaskIcon, testid: "nav-prueba" },
];

// Lista plana (para mantener compatibilidad con cualquier código antiguo que recorra VISIBLE_NAV)
// Módulo de permisos (matriz de roles) de cada entrada de primer nivel.
// Grupos: su `key` ya es el módulo. Entradas sin módulo (Dashboard, Prueba) siempre visibles.
const MODULE_BY_PATH = {
  "/ventas": "ventas", "/cxc": "cobranza", "/calendario": "calendario",
  "/comisiones": "comisiones", "/reportes": "reportes",
  "/metas": "ventas", "/permisos": "permisos", "/formularios": "formularios",
  // /viaticos y /rrhh quedan visibles: tienen autoservicio (mis viáticos / mi perfil)
};
const GROUP_MODULES = ["inventario", "clientes", "compras", "contabilidad", "admin"];
const moduleOf = (it) => (it.children ? (GROUP_MODULES.includes(it.key) ? it.key : null) : MODULE_BY_PATH[it.to] || null);

const VISIBLE_NAV = [DASHBOARD_ITEM, ...OPERACIONES, ...FINANZAS, ...ADMINISTRACION];
// Rol con alcance regional (RTC): solo las pantallas de su función
const RUTAS_ALCANCE = ["/dashboard", "/productos", "/productos/lista-precios", "/inventario/solicitudes-traslado",
  "/clientes", "/clientes/dormidos", "/ventas", "/cxc", "/calendario", "/comisiones", "/viaticos", "/rrhh"];
// Módulos del RTC base: lo demás que el Administrador autorice en el rol se muestra completo
const MODULOS_RTC_BASE = ["ventas", "cobranza", "clientes", "inventario", "viaticos", "calendario", "comisiones"];
const ALL_GROUPS = [...OPERACIONES, ...FINANZAS, ...ADMINISTRACION].filter((x) => x.children);

export default function Sidebar() {
  const { user, logout, hasModule } = useAuth();
  const { isOpen, close } = useSidebar();
  const loc = useLocation();
  const [query, setQuery] = useState("");
  // Cierra el sidebar al hacer click en cualquier link (especialmente útil en tablet/mobile)
  const onNavClick = () => {
    if (typeof window !== "undefined" && window.innerWidth < 1024) close();
  };
  const initialOpen = {};
  ALL_GROUPS.forEach((n) => {
    if (n.matchPaths?.some((p) => loc.pathname.startsWith(p))) initialOpen[n.key] = true;
  });
  const [open, setOpen] = useState(initialOpen);
  const toggle = (k) => setOpen((s) => ({ ...s, [k]: !s[k] }));

  // Filtro por búsqueda (case-insensitive)
  const q = query.trim().toLowerCase();
  const matchesQuery = (label) => !q || label.toLowerCase().includes(q);
  const extraAutorizado = (mod) => Boolean(mod && !MODULOS_RTC_BASE.includes(mod) && hasModule && hasModule(mod, "ver"));
  const allowed = (it, parentMod) => {
    if (it.adminOnly && !(user?.role === "admin" || user?.effective?.is_admin)) return false;   // exclusivo del Administrador General
    if (user?.effective?.alcance_regional && it.to && !RUTAS_ALCANCE.includes(it.to)
        && !extraAutorizado(moduleOf(it) || parentMod)) return false;
    const mod = moduleOf(it);
    return !mod || !hasModule || hasModule(mod, "ver");
  };
  const filterSection = (items) =>
    items
      .map((it) => {
        if (!allowed(it)) return null;
        if (!it.children) return matchesQuery(it.label) ? it : null;
        const filteredChildren = it.children.filter((c) => allowed(c, moduleOf(it)) && (matchesQuery(c.label) || matchesQuery(it.label)));
        if (filteredChildren.length === 0 && !matchesQuery(it.label)) return null;
        return { ...it, children: filteredChildren };
      })
      .filter(Boolean);
  const opSection = useMemo(() => filterSection(OPERACIONES), [q, user]); // eslint-disable-line
  const fiSection = useMemo(() => filterSection(FINANZAS), [q, user]); // eslint-disable-line
  const adSection = useMemo(() => filterSection(ADMINISTRACION), [q, user]); // eslint-disable-line
  const showDashboard = matchesQuery(DASHBOARD_ITEM.label);

  const initials = (user?.name || user?.email || "U")
    .split(/[\s.@]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase())
    .join("") || "U";
  const userPhoto = user?.foto || user?.avatar_url || user?.photoURL || null;

  return (
    <aside
      className={`w-64 fixed h-screen bg-sidebar border-r border-border flex flex-col z-40 transition-transform duration-300 ${isOpen ? "translate-x-0" : "-translate-x-full"}`}
      data-testid="app-sidebar"
      aria-hidden={!isOpen}
    >
      {/* ============ TOP: Usuario + Búsqueda ============ */}
      <div className="px-4 pt-4 pb-3 border-b border-border/40">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2.5 min-w-0 flex-1">
            <div className="relative w-9 h-9 rounded-full bg-gradient-to-br from-agro-green to-agro-green-hover ring-2 ring-agro-green/30 flex-shrink-0 overflow-hidden">
              {userPhoto ? (
                <img src={userPhoto} alt={user?.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-black font-semibold text-xs">
                  {initials}
                </div>
              )}
              <span className="absolute bottom-0 right-0 w-2 h-2 rounded-full bg-agro-green ring-2 ring-sidebar" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-text-primary truncate" data-testid="sidebar-user-name">
                {user?.name || user?.email || "Usuario"}
              </div>
              <div className="text-[10px] text-text-muted uppercase tracking-wider truncate">{user?.role || "operador"}</div>
            </div>
          </div>
          <button
            onClick={close}
            data-testid="sidebar-close-btn"
            className="lg:hidden p-1.5 rounded-md text-text-muted hover:text-text-primary hover:bg-white/[0.05]"
            aria-label="Cerrar menú"
            title="Cerrar menú"
          >
            <XIcon size={16} />
          </button>
        </div>

        {/* Search bar */}
        <div className="relative">
          <MagnifyingGlassIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
          <input
            data-testid="sidebar-search"
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar módulo…"
            className="w-full bg-app/60 border border-border/60 rounded-md pl-8 pr-12 py-2 text-xs placeholder:text-text-muted focus:border-agro-green"
          />
          <span className="absolute right-2 top-1/2 -translate-y-1/2 kbd">⌘ K</span>
        </div>
      </div>

      {/* ============ NAV con secciones ============ */}
      <nav className="flex-1 overflow-y-auto py-2 px-2 space-y-1" data-testid="sidebar-nav">
        {showDashboard && (
          <>
            <div className="sidebar-section-header">DASHBOARD</div>
            <NavLink
              to={DASHBOARD_ITEM.to}
              end
              data-testid={DASHBOARD_ITEM.testid}
              onClick={onNavClick}
              className={({ isActive }) =>
                `group flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-all duration-150 ${
                  isActive ? "sidebar-item-active" : "text-text-secondary hover:text-text-primary hover:bg-white/[0.03]"
                }`
              }
            >
              <DASHBOARD_ITEM.icon size={18} weight={loc.pathname === DASHBOARD_ITEM.to ? "fill" : "regular"} />
              <span>{DASHBOARD_ITEM.label}</span>
            </NavLink>
          </>
        )}

        <SidebarSection title="OPERACIONES" items={opSection} loc={loc} open={open} toggle={toggle} onNavClick={onNavClick} />
        <SidebarSection title="FINANZAS" items={fiSection} loc={loc} open={open} toggle={toggle} onNavClick={onNavClick} />
        <SidebarSection title="ADMINISTRACIÓN" items={adSection} loc={loc} open={open} toggle={toggle} onNavClick={onNavClick} />

        {!showDashboard && opSection.length === 0 && fiSection.length === 0 && adSection.length === 0 && (
          <div className="px-3 py-6 text-center text-xs text-text-muted">Sin resultados para “{query}”</div>
        )}
      </nav>

      {/* ============ FOOTER: Logo + Logout ============ */}
      <div className="border-t border-border/40 p-3 flex items-center gap-3">
        <NavLink to="/dashboard" onClick={onNavClick} className="flex-1 min-w-0 hover:opacity-80 transition" data-testid="sidebar-logo-home">
          <img
            src="/agroport-logo-green.png"
            alt="AGROPORT"
            className="h-8 w-auto object-contain"
            data-testid="sidebar-logo-img"
          />
        </NavLink>
        <button
          onClick={logout}
          data-testid="sidebar-logout-btn"
          className="p-2 rounded-md text-text-muted hover:text-agro-yellow hover:bg-white/[0.05] transition-colors"
          title="Cerrar sesión" aria-label="Cerrar sesión"
        >
          <SignOutIcon size={16} />
        </button>
      </div>
    </aside>
  );
}

function SidebarSection({ title, items, loc, open, toggle, onNavClick }) {
  if (items.length === 0) return null;
  return (
    <>
      <div className="sidebar-section-header">{title}</div>
      {items.map((item) => {
        if (item.children) {
          const isGroupActive = item.matchPaths?.some((p) => loc.pathname.startsWith(p));
          const isOpen = open[item.key];
          return (
            <div key={item.key}>
              <button
                type="button"
                data-testid={item.testid}
                onClick={() => toggle(item.key)}
                className={`group w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-all duration-150 ${
                  isGroupActive
                    ? "sidebar-item-active"
                    : "text-text-secondary hover:text-text-primary hover:bg-white/[0.03]"
                }`}
              >
                <item.icon size={18} weight={isGroupActive ? "fill" : "regular"} />
                <span className="flex-1 text-left">{item.label}</span>
                <CaretRightIcon
                  size={12}
                  className={`transition-transform duration-200 ${isOpen ? "rotate-90" : ""}`}
                />
              </button>
              {isOpen && (
                <div className="ml-3 mt-1 mb-2 pl-3 border-l border-border/60 space-y-0.5" data-testid={`${item.testid}-children`}>
                  {item.children.map((sub) => (
                    <NavLink
                      key={sub.to}
                      to={sub.to}
                      data-testid={sub.testid}
                      onClick={onNavClick}
                      className={({ isActive }) =>
                        `flex items-center gap-2.5 px-3 py-2 rounded-md text-[13px] transition-all duration-150 ${
                          isActive
                            ? "bg-agro-green/15 text-agro-green font-medium"
                            : "text-text-muted hover:text-text-primary hover:bg-white/[0.03]"
                        }`
                      }
                    >
                      <sub.icon size={14} weight={loc.pathname === sub.to ? "fill" : "regular"} />
                      <span>{sub.label}</span>
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          );
        }
        return (
          <NavLink
            key={item.to}
            to={item.to}
            data-testid={item.testid}
            onClick={onNavClick}
            className={({ isActive }) =>
              `group flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-all duration-150 ${
                isActive
                  ? "sidebar-item-active"
                  : "text-text-secondary hover:text-text-primary hover:bg-white/[0.03]"
              }`
            }
          >
            <item.icon size={18} weight={loc.pathname === item.to ? "fill" : "regular"} />
            <span>{item.label}</span>
          </NavLink>
        );
      })}
    </>
  );
}
